import React, { useCallback, useMemo } from "react"
import { Table, Empty } from "antd"
import { useNavigate } from "react-router-dom"
import { ColumnsType } from "antd/es/table"
import { useUserStore, User } from "stores/userStore"
import { useFetchProjectType } from "features/Projects/api/useProjectsQuery"

import getColumns from "./columns"
import {
  getSkeletonColumns,
  getSkeletonData,
} from "shared/ui/LoadingSkeleton/LoadingSkeleton.component"
import { HandledApiError } from "api/queryClient"
import { notify } from "shared/ui/Notifications/notify"
import { ProjectData, PROJECT_TYPE } from "types/project.types"
import { ProjectTreeRow } from "utils/projectTree"

interface ProjectTableProps {
  rows: ProjectTreeRow[]
  searchTerm?: string
  isLoading: boolean
  isArchived: boolean
  handleUnarchive: (projectId: string, owner: string) => Promise<void> | void
  handleArchive: (projectId: string, owner: string) => Promise<void> | void
  onRenameFolder?: (folderPath: string) => void
  onDeleteFolder?: (folderPath: string, count: number) => void
  onMoveProject?: (project: ProjectData) => void
}

// Recursively flattens the tree into a list of project rows. Used when the
// caller is searching — folder grouping is suppressed so matches across folders
// surface in one list.
const flattenProjects = (rows: ProjectTreeRow[]): ProjectTreeRow[] => {
  const out: ProjectTreeRow[] = []
  for (const row of rows) {
    if (row.kind === "project") {
      out.push(row)
    } else {
      out.push(...flattenProjects(row.children))
    }
  }
  return out
}

const matchesSearch = (row: ProjectTreeRow, term: string): boolean => {
  if (row.kind !== "project") return false
  const haystack = (row.metadata?.Name ?? "").toLowerCase()
  return haystack.includes(term.toLowerCase())
}

const ProjectTable: React.FC<ProjectTableProps> = ({
  rows,
  searchTerm,
  isLoading,
  isArchived,
  handleUnarchive,
  handleArchive,
  onRenameFolder,
  onDeleteFolder,
  onMoveProject,
}) => {
  const user = useUserStore((state) => state.user) as User
  const fetchProjectType = useFetchProjectType()
  const navigate = useNavigate()

  const dataSource: ProjectTreeRow[] = useMemo(() => {
    const term = searchTerm?.trim() ?? ""
    if (!term) return rows
    return flattenProjects(rows).filter((row) => matchesSearch(row, term))
  }, [rows, searchTerm])

  const columns: ColumnsType<ProjectTreeRow> = getColumns(
    isArchived,
    handleUnarchive,
    handleArchive,
    user,
    { onRenameFolder, onDeleteFolder, onMoveProject }
  )

  const skeletonColumns = getSkeletonColumns<ProjectTreeRow>(columns)
  const skeletonData = getSkeletonData<ProjectTreeRow>(5) as ProjectTreeRow[]

  const CustomNoData = () => (
    <div className="p-10">
      <Empty description={<span>No Projects Available.</span>} />
    </div>
  )

  const handleRowClick = useCallback(
    async (record: ProjectTreeRow): Promise<void> => {
      if (record.kind !== "project") return
      if (isArchived || !record.id) return
      try {
        const projectType = await fetchProjectType(record.id)
        if (
          projectType &&
          projectType.toLowerCase() === PROJECT_TYPE.DASHBOARD
        ) {
          navigate(`/reporting/${record.id}`)
        } else {
          navigate(`/home/${record.id}`)
        }
      } catch (error) {
        // Sequence errors are silent (HandledApiError); either way fall back
        // to the regular project page.
        if (!(error instanceof HandledApiError)) {
          const errorMessage =
            error instanceof Error ? error.message : "Unknown error"
          notify.error("Error Fetching Project Type!", errorMessage)
        }
        navigate(`/home/${record.id}`)
      }
    },
    [isArchived, fetchProjectType, navigate]
  )

  return (
    <Table<ProjectTreeRow>
      size="small"
      className="project-table"
      rowClassName={(record) => {
        if (record.kind === "folder") return "project-table-folder-row"
        return !isArchived ? "cursor-pointer" : ""
      }}
      rowKey="id"
      dataSource={isLoading ? skeletonData : dataSource}
      columns={isLoading ? skeletonColumns : columns}
      pagination={{
        pageSize: 10,
        hideOnSinglePage: true,
        showSizeChanger: false,
        simple: true,
      }}
      locale={{
        emptyText: <CustomNoData />,
      }}
      onRow={(record) => ({
        onClick: () => !isLoading && handleRowClick(record),
      })}
    />
  )
}

export default ProjectTable
